import React, { useState } from 'react';
import { Alta, Ejecutiva, UpdateAltaPayload } from '../../types/Altas';
import { Button, Modal, Segmented } from '../ui';
import { esDniValido, inputClass, labelClass, limpiarDni } from './altasUi';

interface Props {
  alta: Alta;
  ejecutivas: Ejecutiva[];
  planes: string[];
  onGuardar: (id: number, payload: UpdateAltaPayload) => Promise<boolean>;
  onClose: () => void;
}

/** Corrige ejecutiva, DNI, nombre o plan. Con DNI o plan nuevos, la alta se vuelve a cruzar. */
const EditarAltaModal: React.FC<Props> = ({ alta, ejecutivas, planes, onGuardar, onClose }) => {
  const [ejecutivaId, setEjecutivaId] = useState(String(alta.ejecutivaId));
  const [dni, setDni] = useState(alta.dni);
  const [nombre, setNombre] = useState(alta.nombre ?? '');
  const [plan, setPlan] = useState(alta.planCargado);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dniLimpio = limpiarDni(dni);
  const dniValido = esDniValido(dniLimpio);
  const cambiaPlan = plan !== alta.planCargado;
  const cambiaDni = dniLimpio !== alta.dni;

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const ok = await onGuardar(alta.id, {
        ejecutivaId: Number(ejecutivaId),
        dni: dniLimpio,
        nombre: nombre.trim(),
        plan,
      });
      if (ok) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la corrección');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal titulo={`Corregir alta · DNI ${alta.dni}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label htmlFor="editar-ejecutiva" className={labelClass}>
            Ejecutivo
          </label>
          <select
            id="editar-ejecutiva"
            value={ejecutivaId}
            onChange={(e) => setEjecutivaId(e.target.value)}
            className={inputClass}
          >
            {ejecutivas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}
                {e.activa ? '' : ' (inactiva)'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="editar-dni" className={labelClass}>
            DNI
          </label>
          <input
            id="editar-dni"
            type="text"
            inputMode="numeric"
            value={dni}
            onChange={(e) => setDni(e.target.value)}
            autoComplete="off"
            className={`${inputClass} tabular-nums`}
          />
          {!dniValido && (
            <p className="mt-1.5 text-[11px] text-amber-600">
              El DNI tiene que tener entre 6 y 9 dígitos.
            </p>
          )}
        </div>

        <div>
          <label htmlFor="editar-nombre" className={labelClass}>
            Nombre y apellido
          </label>
          <input
            id="editar-nombre"
            type="text"
            value={nombre}
            maxLength={120}
            onChange={(e) => setNombre(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <span className={labelClass}>Plan vendido</span>
          <Segmented
            options={planes.map((p) => ({ value: p, label: p }))}
            value={plan}
            onChange={setPlan}
            ariaLabel="Plan vendido"
          />
          {(cambiaPlan || cambiaDni) && alta.estado !== 'pendiente' && (
            <p className="mt-1.5 text-[11px] text-slate-400">
              Al cambiar el {cambiaDni ? 'DNI' : 'plan'}, el alta vuelve a pendiente y se verifica
              de nuevo contra Salesforce.
            </p>
          )}
        </div>

        {error && (
          <p role="alert" className="text-sm font-medium text-red-600">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={guardar}
            disabled={guardando || !dniValido}
          >
            Guardar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditarAltaModal;
